import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import './i18n';
import '@/App.css';

import { AuthProvider } from '@/lib/auth';
import { CartProvider } from '@/lib/cart';
import { CurrencyProvider } from '@/lib/currency';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// Home is loaded eagerly (LCP page); everything else is code-split
import Home from '@/pages/Home';

// Non-critical, deferred UI (loaded after main content)
const WhatsAppFAB = lazy(() => import('@/components/WhatsAppFAB'));
const CartDrawer = lazy(() => import('@/components/CartDrawer'));

// Route-level code splitting — each page becomes its own chunk
const Atelier = lazy(() => import('@/pages/Atelier'));
const ProntaConsegna = lazy(() => import('@/pages/ProntaConsegna'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const CheckoutSuccess = lazy(() => import('@/pages/CheckoutSuccess'));
const Login = lazy(() => import('@/pages/Login'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Register = lazy(() => import('@/pages/Register'));
const Account = lazy(() => import('@/pages/Account'));
const Admin = lazy(() => import('@/pages/Admin'));
const Maison = lazy(() => import('@/pages/Maison'));
const Concierge = lazy(() => import('@/pages/Concierge'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Tracking = lazy(() => import('@/pages/Tracking'));
const Review = lazy(() => import('@/pages/Review'));
const EngagementRings = lazy(() => import('@/pages/EngagementRings'));
const WeddingBands = lazy(() => import('@/pages/WeddingBands'));
const WaveRings = lazy(() => import('@/pages/WaveRings'));
const WavePhotoProduct = lazy(() => import('@/pages/WavePhotoProduct'));

// Minimal skeleton for lazy route transitions — matches inline pre-hydration skeleton
const RouteSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-14 h-14 rounded-full border border-[#C9A961] flex items-center justify-center text-[#C9A961] font-serif text-2xl"
         style={{ animation: 'hariziPulse 1.8s ease-in-out infinite' }}>
      H
    </div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

// Remove the pre-hydration skeleton once React has mounted (avoids visible flash)
function RemovePrehydrationSkeleton() {
  useEffect(() => {
    const el = document.getElementById('__prehydration_skeleton');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }, []);
  return null;
}

function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  useEffect(() => {
    document.body.classList.toggle('harizi-public-theme', !isAdmin);
    return () => document.body.classList.remove('harizi-public-theme');
  }, [isAdmin]);
  return (
    <>
      {!isAdmin && <Navbar />}
      <main>{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && (
        <Suspense fallback={null}>
          <WhatsAppFAB />
          <CartDrawer />
        </Suspense>
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RemovePrehydrationSkeleton />
            <Toaster position="top-center" toastOptions={{ style: { background: '#0F3530', color: '#FFF', border: '1px solid #C9A961', borderRadius: 0, fontFamily: 'Outfit, sans-serif' } }} />
            <Layout>
              <Suspense fallback={<RouteSkeleton />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/atelier" element={<Atelier />} />
                  <Route path="/anelli-fidanzamento" element={<EngagementRings />} />
                  <Route path="/fedi-nuziali" element={<WeddingBands />} />
                  <Route path="/anelli-wave" element={<WaveRings />} />
                  <Route path="/pronta-consegna" element={<ProntaConsegna />} />
                  <Route path="/prodotto/wave-classico" element={<WavePhotoProduct />} />
                  <Route path="/prodotto/:slug" element={<ProductDetail />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/checkout/cancel" element={<Home />} />
                  <Route path="/tracking/:orderId" element={<Tracking />} />
                  <Route path="/recensione/:token" element={<Review />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/password-dimenticata" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/legacy" element={<Maison />} />
                  <Route path="/maison" element={<Maison />} />
                  <Route path="/contatti" element={<Concierge />} />
                  <Route path="/concierge" element={<Concierge />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/termini" element={<Terms />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="*" element={<Home />} />
                </Routes>
              </Suspense>
            </Layout>
          </BrowserRouter>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
